

import { createContext, useContext } from 'react';
import type { AuthUser, User, App, AppRequest, Group, OrgSettings } from '@/types';

// Mock data for development
export const mockUsers: User[] = [
  {
    id: '1',
    email: 'admin@company.com',
    name: 'System Admin',
    role: 'admin',
    groups: ['All Company', 'IT Department'],
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    isActive: true,
  },
  {
    id: '2',
    email: 'developer@company.com',
    name: 'John Developer',
    role: 'creator',
    groups: ['All Company', 'Engineering'],
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
    isActive: true,
  },
  {
    id: '3',
    email: 'user1@company.com',
    name: 'Jane User',
    role: 'user',
    groups: ['All Company', 'HR Department'],
    createdAt: new Date('2024-02-01'),
    updatedAt: new Date('2024-02-01'),
    isActive: true,
  },
  {
    id: '4',
    email: 'user2@company.com',
    name: 'Bob Smith',
    role: 'user',
    groups: ['All Company', 'Legal Department'],
    createdAt: new Date('2024-02-10'),
    updatedAt: new Date('2024-02-10'),
    isActive: true,
  },
];

export const mockGroups: Group[] = [
  { id: '1', name: 'All Company', description: 'All employees', memberCount: 100, createdAt: new Date('2024-01-01') },
  { id: '2', name: 'IT Department', description: 'Information Technology', memberCount: 15, createdAt: new Date('2024-01-01') },
  { id: '3', name: 'HR Department', description: 'Human Resources', memberCount: 8, createdAt: new Date('2024-01-01') },
  { id: '4', name: 'Engineering', description: 'Product Development', memberCount: 25, createdAt: new Date('2024-01-01') },
  { id: '5', name: 'Legal Department', description: 'Legal Affairs', memberCount: 5, createdAt: new Date('2024-01-01') },
  { id: '6', name: 'Marketing', description: 'Marketing Department', memberCount: 12, createdAt: new Date('2024-01-01') },
];

export const mockApps: App[] = [
  {
    id: '1',
    name: 'Timesheet System',
    link: 'https://estha.ai/apps/timesheet',
    description: 'Internal system for recording and managing employee work hours',
    categoryTags: ['Tools', 'Productivity'],
    creatorId: '2',
    creatorEmail: 'developer@company.com',
    status: 'approved',
    version: '1.0.0',
    allowedGroups: ['All Company'],
    allowedEmails: [],
    createdAt: new Date('2024-03-01'),
    updatedAt: new Date('2024-03-01'),
  },
  {
    id: '2',
    name: 'HR Recruitment',
    link: 'https://estha.ai/apps/recruitment',
    description: 'Recruitment workflow management tool, HR Department only',
    categoryTags: ['HR', 'Management'],
    creatorId: '2',
    creatorEmail: 'developer@company.com',
    status: 'approved',
    version: '1.2.0',
    allowedGroups: ['HR Department'],
    allowedEmails: [],
    createdAt: new Date('2024-03-15'),
    updatedAt: new Date('2024-03-20'),
  },
];

export const mockAppRequests: AppRequest[] = [
  {
    id: '1',
    type: 'create',
    app: {
      name: 'Contract Approval',
      link: 'https://estha.ai/apps/contract',
      description: 'System for reviewing and approving company contracts',
      categoryTags: ['Legal', 'Approval'],
      allowedGroups: ['Legal Department', 'IT Department'],
      allowedEmails: ['ceo@company.com'],
    },
    creatorId: '2',
    creatorEmail: 'developer@company.com',
    status: 'pending',
    createdAt: new Date('2024-04-01'),
  },
];

export const mockOrgSettings: OrgSettings = {
  id: '1',
  name: 'Estha Enterprise',
  logo: '',
  banner: '',
  primaryColor: '#0ea5e9',
  welcomeMessage: 'Welcome to your Enterprise App Center',
  createdAt: new Date('2024-01-01'),
  updatedAt: new Date('2024-01-01'),
};

// Store interface
export interface Store {
  // Auth
  currentUser: AuthUser | null;
  setCurrentUser: (user: AuthUser | null) => void;

  // Users
  users: User[];
  addUser: (user: User) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  deleteUser: (id: string) => void;
  deleteUsers: (ids: string[]) => void;
  importUsers: (users: User[]) => void;

  // Groups
  groups: Group[];
  addGroup: (group: Group) => void;
  deleteGroup: (id: string) => void;

  // Apps
  apps: App[];
  addApp: (app: App) => void;
  updateApp: (id: string, app: Partial<App>) => void;
  deleteApp: (id: string) => void;

  // App Requests
  appRequests: AppRequest[];
  addAppRequest: (request: AppRequest) => void;
  updateAppRequest: (id: string, request: Partial<AppRequest>) => void;

  // Org Settings
  orgSettings: OrgSettings;
  updateOrgSettings: (settings: Partial<OrgSettings>) => void;
}

export const StoreContext = createContext<Store | null>(null);

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return store;
}
